import { useEffect, useMemo, useRef, useState } from 'react';
import { useAnchoredPopover } from './useAnchoredPopover';
import {
  BLANK, DATE_PRESETS, distinctValues, isFilterActive,
  type BookColumn, type ColumnFilter, type DatePreset, type SortSpec,
} from '../../../../shared/bookGrid';
import type { DashboardRow } from '../../../../shared/types';

const COMPLIANCE_LABEL: Record<string, string> = { pass: 'Current (tick)', outstanding: 'Outstanding (cross)', unknown: 'No date on file' };

/**
 * Excel-autofilter style popover for one grid column: sort A-Z / Z-A plus a
 * filter that suits the column kind (value checklist, text contains, number
 * range, or date presets + from/to). Positioned `fixed` off the header button
 * so the grid's scroll container never clips it.
 */
export function FilterPopover({
  column, anchor, rows, today, filter, sort, onFilter, onSort, onClose,
}: {
  column: BookColumn;
  anchor: HTMLElement;
  /** Rows after every OTHER column's filter, so the value list only offers reachable values (like Excel). */
  rows: DashboardRow[];
  today: string;
  filter: ColumnFilter | undefined;
  sort: SortSpec;
  onFilter: (f: ColumnFilter | undefined) => void;
  onSort: (dir: 'asc' | 'desc') => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const pos = useAnchoredPopover(anchor, ref, onClose);

  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('input, button')?.focus();
  }, []);

  const useValues = column.kind === 'category' || column.kind === 'compliance' || column.kind === 'int';
  const sortActive = sort.key === column.key;

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={`Filter ${column.label}`}
      className="qa-book-pop qa-shadow"
      style={{ top: pos.top, left: pos.left }}
    >
      <div className="flex gap-1 mb-2">
        <button type="button" className={`qa-book-pop__btn flex-1 ${sortActive && sort.dir === 'asc' ? 'is-on' : ''}`} onClick={() => onSort('asc')}>
          Sort {column.kind === 'text' || column.kind === 'category' ? 'A to Z' : 'low to high'}
        </button>
        <button type="button" className={`qa-book-pop__btn flex-1 ${sortActive && sort.dir === 'desc' ? 'is-on' : ''}`} onClick={() => onSort('desc')}>
          Sort {column.kind === 'text' || column.kind === 'category' ? 'Z to A' : 'high to low'}
        </button>
      </div>

      {column.kind === 'text' && <TextFilter filter={filter} onFilter={onFilter} />}
      {useValues && <ValuesFilter column={column} rows={rows} today={today} filter={filter} onFilter={onFilter} />}
      {column.kind === 'money' && <RangeFilter filter={filter} onFilter={onFilter} />}
      {column.kind === 'date' && <DateFilter filter={filter} onFilter={onFilter} />}

      <div className="flex justify-between items-center mt-3 pt-2 border-t border-hairline-paper">
        <button type="button" className="qa-book-pop__link" disabled={!isFilterActive(filter)} onClick={() => onFilter(undefined)}>
          Clear filter
        </button>
        <button type="button" className="qa-book-pop__btn" onClick={onClose}>Done</button>
      </div>
    </div>
  );
}

function TextFilter({ filter, onFilter }: { filter: ColumnFilter | undefined; onFilter: (f: ColumnFilter | undefined) => void }) {
  const value = filter?.type === 'text' ? filter.contains : '';
  return (
    <label className="block text-xs text-ink-mute">
      Contains
      <input
        className="qa-input mt-1 w-full text-sm"
        value={value}
        onChange={(e) => onFilter(e.target.value ? { type: 'text', contains: e.target.value } : undefined)}
      />
    </label>
  );
}

function ValuesFilter({ column, rows, today, filter, onFilter }: {
  column: BookColumn; rows: DashboardRow[]; today: string;
  filter: ColumnFilter | undefined; onFilter: (f: ColumnFilter | undefined) => void;
}) {
  const [search, setSearch] = useState('');
  const options = useMemo(() => distinctValues(rows, column.key, today), [rows, column.key, today]);
  const selected = filter?.type === 'values' ? new Set(filter.values) : null; // null = everything
  const shown = options.filter((o) => !search || displayOf(column, o.label).toLowerCase().includes(search.toLowerCase()));
  const isOn = (label: string) => (selected ? selected.has(label) : true);

  function toggle(label: string) {
    const next = new Set(selected ?? options.map((o) => o.label));
    if (next.has(label)) next.delete(label); else next.add(label);
    onFilter(next.size === options.length ? undefined : { type: 'values', values: [...next] });
  }

  const allOn = !selected;
  return (
    <div>
      {options.length > 8 && (
        <input className="qa-input w-full text-sm mb-2" placeholder="Search values" aria-label="Search values"
          value={search} onChange={(e) => setSearch(e.target.value)} />
      )}
      <label className="qa-book-pop__opt font-medium">
        <input type="checkbox" checked={allOn} onChange={() => onFilter(allOn ? { type: 'values', values: [] } : undefined)} />
        (Select all)
      </label>
      <div className="max-h-56 overflow-y-auto">
        {shown.map((o) => (
          <label key={o.label} className="qa-book-pop__opt">
            <input type="checkbox" checked={isOn(o.label)} onChange={() => toggle(o.label)} />
            <span className="flex-1 truncate">{displayOf(column, o.label)}</span>
            <span className="qa-mono text-[10px] text-ink-mute">{o.count}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function displayOf(column: BookColumn, label: string): string {
  if (column.kind === 'compliance' && label !== BLANK) return COMPLIANCE_LABEL[label] ?? label;
  return label;
}

function RangeFilter({ filter, onFilter }: { filter: ColumnFilter | undefined; onFilter: (f: ColumnFilter | undefined) => void }) {
  const f = filter?.type === 'range' ? filter : { type: 'range' as const, min: null, max: null };
  const set = (patch: { min?: number | null; max?: number | null }) => {
    const next = { ...f, ...patch };
    onFilter(next.min == null && next.max == null ? undefined : next);
  };
  const parse = (s: string) => (s.trim() === '' ? null : Number(s.replace(/[$,\s]/g, '')));
  return (
    <div className="grid grid-cols-2 gap-2">
      <label className="text-xs text-ink-mute">At least
        <input className="qa-input mt-1 w-full text-sm qa-mono" inputMode="decimal" defaultValue={f.min ?? ''}
          onChange={(e) => { const v = parse(e.target.value); if (v === null || Number.isFinite(v)) set({ min: v }); }} />
      </label>
      <label className="text-xs text-ink-mute">At most
        <input className="qa-input mt-1 w-full text-sm qa-mono" inputMode="decimal" defaultValue={f.max ?? ''}
          onChange={(e) => { const v = parse(e.target.value); if (v === null || Number.isFinite(v)) set({ max: v }); }} />
      </label>
    </div>
  );
}

function DateFilter({ filter, onFilter }: { filter: ColumnFilter | undefined; onFilter: (f: ColumnFilter | undefined) => void }) {
  const f = filter?.type === 'date' ? filter : { type: 'date' as const, preset: null, from: null, to: null };
  const set = (patch: { preset?: DatePreset | null; from?: string | null; to?: string | null }) => {
    const next = { ...f, ...patch };
    onFilter(!next.preset && !next.from && !next.to ? undefined : next);
  };
  return (
    <div>
      <div className="grid grid-cols-2 gap-1 mb-2">
        {DATE_PRESETS.map((p) => (
          <button key={p.id} type="button"
            className={`qa-book-pop__btn text-left ${f.preset === p.id ? 'is-on' : ''}`}
            aria-pressed={f.preset === p.id}
            onClick={() => set({ preset: f.preset === p.id ? null : p.id })}>
            {p.label}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs text-ink-mute">From
          <input type="date" className="qa-input mt-1 w-full text-sm" value={f.from ?? ''} onChange={(e) => set({ from: e.target.value || null })} />
        </label>
        <label className="text-xs text-ink-mute">To
          <input type="date" className="qa-input mt-1 w-full text-sm" value={f.to ?? ''} onChange={(e) => set({ to: e.target.value || null })} />
        </label>
      </div>
    </div>
  );
}
